import { Box } from '@mui/material'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useGetIndustriesQuery, useGetUsersQuery } from '../../../app/redux-fetch/apiQuery'
import { useGetCurrenciesByTenantIdQuery } from '../../../app/redux-fetch/apiOrders'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import SwitchWithLabels from '../../../components/Switches/SwitchWithLabels'
import { SectionTitleTypography } from '../../../components/Typographies/styles'
import { ICustomer } from '../../types'
import { CustomerFormData } from './Components/validationSchema'
import messages from './messages'

type CustomerDetailProps = {
  customer?: ICustomer
  isDisabled?: boolean
  isNew?: boolean
}

const CustomerDetail = (props: CustomerDetailProps) => {
  const { customer, isDisabled = false } = props
  const { formatMessage } = useIntl()
  const { watch, setValue } = useFormContext<CustomerFormData>()
  const { tenantId } = useLoggedInUser()
  const { data: industries } = useGetIndustriesQuery(undefined, { skip: isDisabled })
  const { data: users = [] } = useGetUsersQuery(
    { tenantId, userGroupName: 'Customer Account Managers' },
    { skip: isDisabled }
  )
  const { data: currencies = [] } = useGetCurrenciesByTenantIdQuery(
    {
      tenantId,
      hideDisabled: true,
      tenantOnly: true,
    },
    { skip: !tenantId || isDisabled }
  )

  const values = watch()

  const currenciesOptions = useMemo(() => {
    const options = currencies
      ?.filter((currency: any) => !currency.customerId)
      .map((currency: any) => ({
        label: `${currency.iso3} - ${currency.localisedSymbol}`,
        value: currency.id,
      })) ?? []

    if (customer?.currency && customer.defaultCurrencyId != null && !options.some((o: any) => o.value === customer.defaultCurrencyId)) {
      options.push({
        label: `${customer.currency.iso3 ?? ''} - ${customer.currency.localisedSymbol ?? ''}`.replace(/^ - /, ''),
        value: customer.defaultCurrencyId,
      })
    }

    return options
  }, [currencies, customer?.currency, customer?.defaultCurrencyId])

  const userOptions = useMemo(() => {
    const options = users.map((user: any) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id
    }))

    if (customer?.accountOwnerId != null && !options.some((o: any) => o.value === customer.accountOwnerId)) {
      const label = `${customer.accountOwnerFirstName ?? ''} ${customer.accountOwnerLastName ?? ''}`.trim()
      if (label) {
        options.push({ label, value: customer.accountOwnerId })
      }
    }

    return options
  }, [users, customer?.accountOwnerId, customer?.accountOwnerFirstName, customer?.accountOwnerLastName])

  const industryOptions = useMemo(() => {
    const options = (industries ?? []).map((industry: any) => ({
      label: industry.name,
      value: industry.id
    }))

    if (customer?.industry && customer.industryId != null && !options.some((o: any) => o.value === customer.industryId)) {
      options.push({ label: customer.industry.name, value: customer.industryId })
    }

    return options
  }, [industries, customer?.industry, customer?.industryId])

  return (
    <Box sx={{ pb: '16px' }}>
      <Box sx={{ mb: '16px' }}>
        <SectionTitleTypography>{formatMessage(messages.customerDetail)}</SectionTitleTypography>
      </Box>

      <FormInput
        name="name"
        label={formatMessage(messages.customerName)}
        required
        disabled={isDisabled}
        maxLength={250}
      />

      <FormSelect
        name="accountOwnerId"
        label={formatMessage(messages.accountOwner)}
        options={userOptions}
        required
        disabled={isDisabled}
        placeholder="Please select an account manager"
      />

      <FormInput
        name="alias"
        label={formatMessage(messages.customerAlias)}
        disabled={isDisabled}
        maxLength={250}
      />

      <FormInput
        name="accountNumber"
        label={formatMessage(messages.accountNumber)}
        disabled={isDisabled}
        maxLength={50}
      />

      <FormInput
        name="email"
        label={formatMessage(messages.customerEmail)}
        type="email"
        disabled={isDisabled}
        maxLength={250}
      />

      <FormInput
        name="secondaryEmail"
        label={formatMessage(messages.secondaryEmail)}
        type="email"
        disabled={isDisabled}
        maxLength={250}
      />

      <Box display='flex' columnGap={2} sx={{ mb: '16px' }}>
        <Box flex={1}>
          <FormInput
            name="registrationNumber"
            label={formatMessage(messages.companyRegistrationNumber)}
            disabled={isDisabled}
            maxLength={50}
          />
        </Box>
        <Box flex={1}>
          <FormInput
            name="vatNumber"
            label={formatMessage(messages.VATNumber)}
            disabled={isDisabled}
            maxLength={50}
          />
        </Box>
      </Box>

      <FormSelect
        name="industryId"
        label={formatMessage(messages.industry)}
        options={industryOptions}
        disabled={isDisabled}
      />

      <FormSelect
        name="defaultCurrencyId"
        label={formatMessage(messages.defaultCurrency)}
        options={currenciesOptions}
        required
        disabled={isDisabled}
        placeholder="Please select a default currency"
      />

      <FormInput
        name="webSite"
        label={formatMessage(messages.webSite)}
        disabled={isDisabled}
        maxLength={50}
      />

      <Box sx={{ mb: 2 }}>
        <SwitchWithLabels
          value={values.disabled || false}
          onChange={(value) => setValue('disabled', value)}
          labelBefore="Enabled"
          labelAfter="Disabled"
          disabled={isDisabled}
        />
      </Box>
    </Box>
  )
}

export default CustomerDetail
